/**
 * J4K Infinite Scroll
 * Automatically loads more products as the user scrolls down on
 * collection and search pages. Uses IntersectionObserver for
 * performant scroll detection.
 */
(function () {
  'use strict';

  var SENTINEL_CLASS = 'j4k-infinite-scroll__sentinel';
  var SPINNER_CLASS = 'j4k-infinite-scroll__spinner';
  var SPINNER_VISIBLE = 'j4k-infinite-scroll__spinner--visible';
  var END_CLASS = 'j4k-infinite-scroll__end';
  var RETRY_CLASS = 'j4k-infinite-scroll__retry';
  var STORAGE_KEY = 'j4k-scroll-state';
  var PRELOAD_BUFFER = 4000;       // px before bottom to trigger next page load
  var FETCH_TIMEOUT = 10000;       // ms before aborting a page fetch
  var SPINNER_DELAY = 500;         // ms before showing spinner (hide fast loads)

  var state = {
    currentPage: 1,
    totalPages: 1,
    isLoading: false,
    observer: null,
    sentinel: null,
    spinner: null,
    grid: null,
    container: null,
    abortController: null
  };

  function init() {
    // Check if infinite scroll is enabled on this page
    state.container = document.querySelector('[data-infinite-scroll="true"]');
    if (!state.container) return;

    // Find the product grid
    state.grid = state.container.querySelector('#CollectionAjaxContent .grid--uniform') ||
                 state.container.querySelector('.grid--uniform');
    if (!state.grid) return;

    // Read pagination state
    var pagination = state.container.querySelector('.pagination[data-total-pages]');
    if (!pagination) return;

    state.totalPages = parseInt(pagination.getAttribute('data-total-pages'), 10) || 1;
    state.currentPage = parseInt(pagination.getAttribute('data-current-page'), 10) || 1;

    // If only one page, nothing to do
    if (state.totalPages <= 1) return;

    // If we're already on the last page, nothing to load
    if (state.currentPage >= state.totalPages) return;

    // Check for saved scroll state (restore after refresh)
    var saved = getSavedState();
    if (saved && saved.path === window.location.pathname && saved.page > 1) {
      restorePages(saved);
      return;
    }

    createSpinner();
    createSentinel();
    connectObserver();
    saveScrollPosition();
  }

  function getSavedState() {
    try {
      var data = sessionStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null; // Storage unavailable
    }
  }

  function saveState() {
    try {
      // Find the first visible product index for reliable scroll restore
      var productIndex = 0;
      var items = state.grid ? state.grid.querySelectorAll('.grid__item') : [];
      for (var i = 0; i < items.length; i++) {
        var rect = items[i].getBoundingClientRect();
        if (rect.top >= 0) {
          productIndex = i;
          break;
        }
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        path: window.location.pathname,
        page: state.currentPage,
        productIndex: productIndex
      }));
    } catch (e) { /* Storage unavailable */ }
  }

  function clearState() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* Storage unavailable */ }
  }

  function saveScrollPosition() {
    // Remove previous listener if any to prevent duplicates
    if (state.scrollHandler) {
      window.removeEventListener('scroll', state.scrollHandler);
    }
    var scrollTimer = null;
    state.scrollHandler = function () {
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        if (state.currentPage > 1) {
          saveState();
        }
      }, 200);
    };
    window.addEventListener('scroll', state.scrollHandler);
  }

  function restorePages(saved) {
    var targetPage = saved.page;
    var targetProductIndex = saved.productIndex || 0;

    // Clear saved state so it doesn't loop on error
    clearState();

    // Fetch all pages in parallel for instant restore
    var fetches = [];
    for (var p = 2; p <= targetPage; p++) {
      fetches.push({ page: p, url: buildUrl(p) });
    }

    Promise.all(fetches.map(function (f) {
      return fetch(f.url)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) { return { page: f.page, html: html }; })
        .catch(function () { return { page: f.page, html: '' }; });
    }))
    .then(function (results) {
      // Sort by page number to append in order
      results.sort(function (a, b) { return a.page - b.page; });

      results.forEach(function (result) {
        if (!result.html) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(result.html, 'text/html');
        var fetchedGrid = doc.querySelector('#CollectionAjaxContent .grid--uniform') ||
                          doc.querySelector('.grid--uniform');
        if (!fetchedGrid) return;

        var items = fetchedGrid.querySelectorAll('.grid__item');
        var fragment = document.createDocumentFragment();
        items.forEach(function (item) {
          fragment.appendChild(item.cloneNode(true));
        });
        state.grid.appendChild(fragment);
      });

      state.currentPage = targetPage;
      reinitComponents();
      updateRelNext();

      // Scroll to the saved product element
      var allItems = state.grid.querySelectorAll('.grid__item');
      if (allItems[targetProductIndex]) {
        allItems[targetProductIndex].scrollIntoView({ behavior: 'instant', block: 'start' });
      }

      // Continue with normal infinite scroll
      if (state.currentPage < state.totalPages) {
        createSpinner();
        createSentinel();
        connectObserver();
        saveScrollPosition();
      }
    })
    .catch(function () {
      createSpinner();
      createSentinel();
      connectObserver();
      saveScrollPosition();
    });
  }

  function createSpinner() {
    // Remove existing spinner if any
    removeElement('.' + SPINNER_CLASS);

    var spinner = document.createElement('div');
    spinner.className = SPINNER_CLASS;
    spinner.innerHTML =
      '<div class="j4k-infinite-scroll__spinner-dots">' +
        '<span class="j4k-infinite-scroll__spinner-dot"></span>' +
        '<span class="j4k-infinite-scroll__spinner-dot"></span>' +
        '<span class="j4k-infinite-scroll__spinner-dot"></span>' +
      '</div>';
    spinner.setAttribute('aria-hidden', 'true');

    // Insert after the grid
    state.grid.parentNode.insertBefore(spinner, state.grid.nextSibling);
    state.spinner = spinner;
  }

  function createSentinel() {
    // Remove existing sentinel if any
    removeElement('.' + SENTINEL_CLASS);

    var sentinel = document.createElement('div');
    sentinel.className = SENTINEL_CLASS;

    // Insert after spinner (or after grid if no spinner)
    var ref = state.spinner || state.grid;
    ref.parentNode.insertBefore(sentinel, ref.nextSibling);
    state.sentinel = sentinel;
  }

  function connectObserver() {
    if (state.observer) {
      state.observer.disconnect();
    }

    state.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !state.isLoading) {
          loadNextPage();
        }
      });
    }, {
      rootMargin: '0px 0px ' + PRELOAD_BUFFER + 'px 0px'
    });

    if (state.sentinel) {
      state.observer.observe(state.sentinel);
    }
  }

  function loadNextPage() {
    if (state.isLoading || state.currentPage >= state.totalPages) return;

    state.isLoading = true;
    state.spinnerTimeout = setTimeout(function () {
      showSpinner();
    }, SPINNER_DELAY);
    removeElement('.' + RETRY_CLASS);

    var nextPage = state.currentPage + 1;
    var url = buildUrl(nextPage);

    // Abort any previous request
    if (state.abortController) {
      state.abortController.abort();
    }
    state.abortController = new AbortController();

    var timeoutId = setTimeout(function () {
      if (state.abortController) state.abortController.abort();
    }, FETCH_TIMEOUT);

    fetch(url, { signal: state.abortController.signal })
      .then(function (response) {
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Find grid items in the fetched page
        var fetchedGrid = doc.querySelector('#CollectionAjaxContent .grid--uniform') ||
                          doc.querySelector('.grid--uniform');

        if (!fetchedGrid) {
          onLastPage();
          return;
        }

        var items = fetchedGrid.querySelectorAll('.grid__item');
        if (items.length === 0) {
          onLastPage();
          return;
        }

        // Append each item to the current grid
        var fragment = document.createDocumentFragment();
        items.forEach(function (item) {
          fragment.appendChild(item.cloneNode(true));
        });
        state.grid.appendChild(fragment);

        // Update state
        state.currentPage = nextPage;

        // Save state for scroll restoration on refresh
        saveState();

        // Re-initialize theme components on new items
        reinitComponents();

        // Update link rel="next"
        updateRelNext();

        // Clear spinner timeout so it never shows if load was fast
        if (state.spinnerTimeout) {
          clearTimeout(state.spinnerTimeout);
          state.spinnerTimeout = null;
        }

        // Check if we've reached the end
        if (state.currentPage >= state.totalPages) {
          onLastPage();
        } else {
          hideSpinner();
          state.isLoading = false;
        }
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        if (state.spinnerTimeout) {
          clearTimeout(state.spinnerTimeout);
          state.spinnerTimeout = null;
        }
        if (err.name === 'AbortError') {
          // Request was aborted (timeout or new request)
          hideSpinner();
          state.isLoading = false;
          return;
        }
        hideSpinner();
        state.isLoading = false;
        showRetry();
      });
  }

  function buildUrl(page) {
    var params = new URLSearchParams(window.location.search);
    if (page > 1) {
      params.set('page', page);
    } else {
      params.delete('page');
    }
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function showSpinner() {
    if (state.spinner) {
      state.spinner.classList.add(SPINNER_VISIBLE);
    }
  }

  function hideSpinner() {
    if (state.spinner) {
      state.spinner.classList.remove(SPINNER_VISIBLE);
    }
  }

  function onLastPage() {
    hideSpinner();
    state.isLoading = false;

    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }

    removeElement('.' + SENTINEL_CLASS);
    removeElement('.' + SPINNER_CLASS);

    // End message disabled — Nike-style silent end
    // var endMsg = document.createElement('div');
    // endMsg.className = END_CLASS;
    // endMsg.textContent = "You\u2019ve seen it all";
    // state.grid.parentNode.insertBefore(endMsg, state.grid.nextSibling);
  }

  function showRetry() {
    removeElement('.' + RETRY_CLASS);

    var retry = document.createElement('div');
    retry.className = RETRY_CLASS;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Something went wrong. Tap to retry';
    btn.addEventListener('click', function () {
      removeElement('.' + RETRY_CLASS);
      loadNextPage();
    });

    retry.appendChild(btn);
    var ref = state.spinner || state.grid;
    ref.parentNode.insertBefore(retry, ref.nextSibling);
  }

  function reinitComponents() {
    // Refresh AOS (scroll animations)
    if (typeof AOS !== 'undefined' && AOS.refreshHard) {
      AOS.refreshHard();
    }

    // Re-init quick shop overlays if theme supports it
    if (typeof theme !== 'undefined') {
      if (typeof theme.reinitProductGridItem === 'function') {
        theme.reinitProductGridItem();
      }
      // Re-init collapsibles (e.g., quick-shop drawers)
      if (theme.collapsibles && typeof theme.collapsibles.init === 'function') {
        theme.collapsibles.init();
      }
    }

    // Dispatch event for any external listeners
    document.dispatchEvent(new CustomEvent('infinite-scroll:page-loaded', {
      detail: { page: state.currentPage }
    }));
  }

  function updateRelNext() {
    var existing = document.querySelector('link[rel="next"]');
    if (state.currentPage >= state.totalPages) {
      // No more pages — remove the link
      if (existing) existing.remove();
    } else {
      var nextUrl = buildUrl(state.currentPage + 1);
      if (existing) {
        existing.setAttribute('href', nextUrl);
      }
    }
  }

  function removeElement(selector) {
    var el = document.querySelector(selector);
    if (el) el.remove();
  }

  function reset() {
    // Disconnect existing observer
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }

    // Abort any in-flight request
    if (state.abortController) {
      state.abortController.abort();
      state.abortController = null;
    }

    state.isLoading = false;

    // Clean up existing elements
    removeElement('.' + SENTINEL_CLASS);
    removeElement('.' + SPINNER_CLASS);
    removeElement('.' + END_CLASS);
    removeElement('.' + RETRY_CLASS);

    state.sentinel = null;
    state.spinner = null;

    // Re-find the grid (DOM was likely replaced)
    state.grid = null;
    if (state.container) {
      state.grid = state.container.querySelector('#CollectionAjaxContent .grid--uniform') ||
                   state.container.querySelector('.grid--uniform');
    }

    if (!state.grid) return;

    // Re-read pagination state
    var pagination = state.container.querySelector('.pagination[data-total-pages]');
    if (!pagination) return;

    state.totalPages = parseInt(pagination.getAttribute('data-total-pages'), 10) || 1;
    state.currentPage = parseInt(pagination.getAttribute('data-current-page'), 10) || 1;

    if (state.totalPages <= 1 || state.currentPage >= state.totalPages) return;

    createSpinner();
    createSentinel();
    connectObserver();
  }

  // Listen for filter/sort AJAX reloads
  document.addEventListener('collection:reloaded', reset);

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
